/**
 * Фабрика рёбер (edges) X6: создание, обновление и удаление связей с управлением жизненным циклом.
 * Конфиг маркеров по типу связи ArchiMate — в edgeConfig.
 */
import {
	buildEdgeLineAttrs,
	canonicalizeRelationshipType,
	getEdgeMarkerConfigForRelationshipType,
} from './edgeConfig';
import {buildX6RouterConfig} from './constants';

/**
 * @typedef {Object} CreateEdgeOptions
 * @property {string} [id]
 * @property {string} source — id ячейки-источника
 * @property {string} target — id ячейки-цели
 * @property {Array<{x: number, y: number}>} [vertices]
 * @property {string} [relationshipType] — Association, Flow, Access, …
 * @property {string} [accessType] — для Access: Read, Write, ReadWrite
 * @property {string} [lineColor]
 * @property {string} [label]
 */

/**
 * @typedef {Object} UpdateEdgeOptions
 * @property {string} [relationshipType]
 * @property {string} [accessType]
 * @property {string} [lineColor]
 * @property {number} [lineWidth]
 * @property {string} [label]
 * @property {Array<{x: number, y: number}>} [vertices]
 */

export default class EdgeFactory {
	/**
	 * @param {import('../ModelerX6').default} modeler — экземпляр ModelerX6 (getGraph)
	 */
	constructor(modeler) {
		this._modeler = modeler;
		/** @type {Array<(options: CreateEdgeOptions) => CreateEdgeOptions | void>} */
		this._onBeforeCreate = [];
		/** @type {Array<(edge: import('@antv/x6').Edge, options: CreateEdgeOptions) => void>} */
		this._onAfterCreate = [];
		/** @type {Array<(edge: import('@antv/x6').Edge, options: UpdateEdgeOptions) => UpdateEdgeOptions | void>} */
		this._onBeforeUpdate = [];
		/** @type {Array<(edge: import('@antv/x6').Edge, options: UpdateEdgeOptions) => void>} */
		this._onAfterUpdate = [];
		/** @type {Array<(edge: import('@antv/x6').Edge) => void>} */
		this._onBeforeRemove = [];
		/** @type {Array<(edgeId: string) => void>} */
		this._onAfterRemove = [];
	}

	/**
	 * Построить конфиг ребра без добавления на граф.
	 * @param {CreateEdgeOptions} options
	 * @returns {Object} options для graph.addEdge()
	 */
	create(options = {}) {
		const {
			id,
			source,
			target,
			vertices = [],
			relationshipType = 'Association',
			accessType,
			lineColor,
			label,
		} = options;

		const lineAttrs = buildEdgeLineAttrs(relationshipType, {accessType, lineColor});
		const edgeAttrs = {line: lineAttrs};
		const relCanon = canonicalizeRelationshipType(relationshipType);

		const routerName = this._modeler?.getConnectionRouter?.() ?? 'normal';
		return {
			id,
			zIndex: 10000,
			source: {cell: source, anchor: 'orth', connectionPoint: 'boundary'},
			target: {cell: target, anchor: 'orth', connectionPoint: 'boundary'},
			vertices: vertices.length ? vertices : undefined,
			attrs: edgeAttrs,
			data: {
				relationshipType: relCanon,
				accessType: accessType ?? null,
			},
			connector: {name: 'jumpover', args: {size: 6, type: 'arc'}},
			router: buildX6RouterConfig(routerName),
			label: typeof label === 'string' ? label : (label ?? ''),
		};
	}

	/**
	 * Создать ребро и добавить на граф.
	 * @param {CreateEdgeOptions} options
	 * @returns {import('@antv/x6').Edge}
	 */
	createAndAdd(options = {}) {
		let opts = {...options};
		for (const fn of this._onBeforeCreate) {
			const next = fn(opts);
			if (next != null) opts = next;
		}

		const edgeConfig = this.create(opts);
		const graph = this._modeler.getGraph();
		if (!graph) {
			throw new Error('Graph is not initialized');
		}

		const addOpts = {};
		const edge = graph.addEdge(edgeConfig, addOpts);

		for (const fn of this._onAfterCreate) {
			fn(edge, opts);
		}

		return edge;
	}

	/**
	 * Обновить attrs ребра (маркеры, цвет, подпись). Не меняет source/target/vertices.
	 * @param {import('@antv/x6').Edge} edge
	 * @param {UpdateEdgeOptions} [options]
	 */
	update(edge, options = {}) {
		if (!edge || typeof edge.isEdge !== 'function' || !edge.isEdge()) return;

		let opts = {...options};
		for (const fn of this._onBeforeUpdate) {
			const next = fn(edge, opts);
			if (next != null) opts = next;
		}

		const relationshipType = opts.relationshipType ?? 'Association';
		const accessType = opts.accessType;
		const lineColor = opts.lineColor;
		const lineWidth = opts.lineWidth;

		edge.attr({
			line: buildEdgeLineAttrs(relationshipType, {accessType, lineColor, lineWidth}),
			label: {
				text: opts.label,
			},
		});

		if (
			opts.vertices != null &&
			Array.isArray(opts.vertices) &&
			typeof edge.setVertices === 'function'
		) {
			edge.setVertices(opts.vertices);
		}

		for (const fn of this._onAfterUpdate) {
			fn(edge, opts);
		}
	}

	/**
	 * Обновить только маркеры и стиль линии по типу связи (после смены relationshipType/accessType в модели).
	 * Явно задаёт все атрибуты линии (как при создании ребра) и выставляет их по путям,
	 * чтобы X6 гарантированно обновил вид и не сохранял старые значения из-за merge/кеша.
	 * @param {import('@antv/x6').Edge} edge
	 * @param {string} [relationshipType]
	 * @param {string} [accessType]
	 * @param {string} [lineColor]
	 */
	refreshMarkers(edge, relationshipType = 'Association', accessType, lineColor) {
		if (!edge || typeof edge.isEdge !== 'function' || !edge.isEdge()) return;

		edge.attr({
			line: buildEdgeLineAttrs(canonicalizeRelationshipType(relationshipType), {
				accessType,
				lineColor,
			}),
		});
	}

	/**
	 * Клонирует attrs линии (новые ссылки для sourceMarker/targetMarker),
	 * чтобы X6 зафиксировал изменение и перерисовал ребро.
	 * @private
	 */
	_cloneLineAttrs(lineAttrs) {
		return {
			stroke: lineAttrs.stroke ?? '#000000',
			strokeWidth: lineAttrs.strokeWidth ?? 1,
			// для «нет маркера» оставляем '' (в X6 снимается через attr('line/…Marker', ''))
			sourceMarker:
				lineAttrs.sourceMarker && typeof lineAttrs.sourceMarker === 'object'
					? {...lineAttrs.sourceMarker}
					: (lineAttrs.sourceMarker ?? ''),
			targetMarker:
				lineAttrs.targetMarker && typeof lineAttrs.targetMarker === 'object'
					? {...lineAttrs.targetMarker}
					: (lineAttrs.targetMarker ?? ''),
			strokeDasharray: lineAttrs.strokeDasharray ?? 0,
		};
	}

	/**
	 * Удалить ребро с графа.
	 * @param {import('@antv/x6').Edge | string} edgeOrId
	 */
	remove(edgeOrId) {
		const graph = this._modeler.getGraph();
		if (!graph) return;

		const edge =
			typeof edgeOrId === 'string'
				? (graph.getCellById?.(edgeOrId) ?? graph.model?.getCellById?.(edgeOrId))
				: edgeOrId;

		if (!edge || (typeof edge.isEdge === 'function' && !edge.isEdge())) return;

		for (const fn of this._onBeforeRemove) {
			fn(edge);
		}

		const id = edge.id;
		graph.removeCell(edge);

		for (const fn of this._onAfterRemove) {
			fn(id);
		}
	}

	// --- Lifecycle hooks ---

	onBeforeCreate(fn) {
		this._onBeforeCreate.push(fn);
		return () => {
			const i = this._onBeforeCreate.indexOf(fn);
			if (i >= 0) this._onBeforeCreate.splice(i, 1);
		};
	}

	onAfterCreate(fn) {
		this._onAfterCreate.push(fn);
		return () => {
			const i = this._onAfterCreate.indexOf(fn);
			if (i >= 0) this._onAfterCreate.splice(i, 1);
		};
	}

	onBeforeUpdate(fn) {
		this._onBeforeUpdate.push(fn);
		return () => {
			const i = this._onBeforeUpdate.indexOf(fn);
			if (i >= 0) this._onBeforeUpdate.splice(i, 1);
		};
	}

	onAfterUpdate(fn) {
		this._onAfterUpdate.push(fn);
		return () => {
			const i = this._onAfterUpdate.indexOf(fn);
			if (i >= 0) this._onAfterUpdate.splice(i, 1);
		};
	}

	onBeforeRemove(fn) {
		this._onBeforeRemove.push(fn);
		return () => {
			const i = this._onBeforeRemove.indexOf(fn);
			if (i >= 0) this._onBeforeRemove.splice(i, 1);
		};
	}

	onAfterRemove(fn) {
		this._onAfterRemove.push(fn);
		return () => {
			const i = this._onAfterRemove.indexOf(fn);
			if (i >= 0) this._onAfterRemove.splice(i, 1);
		};
	}
}

export {getEdgeMarkerConfigForRelationshipType};
